import React from "react";

// Tiny, dependency-free Markdown renderer for What's New post bodies.
//
// Why not react-markdown: the platform has no markdown library, and the
// maintained react-markdown / remark-gfm releases are ESM-only, which breaks
// CRA (react-scripts 5) Jest + production build without ejecting. Post bodies
// are platform-authored (admin only) and use a small, predictable subset, so a
// focused renderer is the simplest correct solution and keeps the build green.
//
// Safety: we build a React element tree (never dangerouslySetInnerHTML), so no
// raw HTML from the body can execute. Links are restricted to http(s)/mailto.
//
// Supported: #/##/### headings, unordered (-, *) and ordered (1.) lists,
// blockquotes (>), paragraphs, blank-line separation; inline **bold**,
// *italic*, `code`, and [text](url).

const SAFE_URL = /^(https?:\/\/|mailto:|\/)/i;

// ── Inline parsing → array of React nodes ────────────────────────────────────
function renderInline(text, keyPrefix) {
  const nodes = [];
  let remaining = text;
  let i = 0;
  // Ordered by specificity; each pattern captures its full match so we can
  // slice around it and recurse on the inner text where relevant.
  const patterns = [
    { re: /\[([^\]]+)\]\(([^)\s]+)\)/, kind: "link" },
    { re: /\*\*([^*]+)\*\*/, kind: "bold" },
    { re: /(?<!\*)\*(?!\*)([^*]+)\*(?!\*)/, kind: "italic" },
    { re: /`([^`]+)`/, kind: "code" },
  ];

  while (remaining) {
    let best = null;
    for (const p of patterns) {
      const m = p.re.exec(remaining);
      if (m && (best === null || m.index < best.m.index)) best = { p, m };
    }
    if (!best) {
      nodes.push(remaining);
      break;
    }
    const { p, m } = best;
    if (m.index > 0) nodes.push(remaining.slice(0, m.index));
    const key = `${keyPrefix}-i${i++}`;
    if (p.kind === "link") {
      const url = m[2];
      if (SAFE_URL.test(url)) {
        const external = /^https?:/i.test(url);
        nodes.push(
          <a
            key={key}
            href={url}
            className="text-[#2283FF] hover:text-[#1668D9] underline underline-offset-2"
            {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
          >
            {m[1]}
          </a>,
        );
      } else {
        nodes.push(m[1]); // unsafe scheme — render label as plain text
      }
    } else if (p.kind === "bold") {
      nodes.push(
        <strong key={key} className="font-bold text-[#22334B]">
          {renderInline(m[1], key)}
        </strong>,
      );
    } else if (p.kind === "italic") {
      nodes.push(
        <em key={key} className="italic">
          {renderInline(m[1], key)}
        </em>,
      );
    } else if (p.kind === "code") {
      nodes.push(
        <code
          key={key}
          className="px-1 py-0.5 rounded bg-[#EEF2F7] text-[#33455E] text-[0.9em] font-mono"
        >
          {m[1]}
        </code>,
      );
    }
    remaining = remaining.slice(m.index + m[0].length);
  }
  return nodes;
}

// ── Block parsing → array of block elements ──────────────────────────────────
function parseBlocks(src) {
  const lines = String(src || "").replace(/\r\n?/g, "\n").split("\n");
  const blocks = [];
  let i = 0;

  const isUL = (l) => /^\s*[-*]\s+/.test(l);
  const isOL = (l) => /^\s*\d+\.\s+/.test(l);

  while (i < lines.length) {
    let line = lines[i];

    if (!line.trim()) {
      i += 1;
      continue;
    }

    // Headings
    const h = /^(#{1,3})\s+(.*)$/.exec(line);
    if (h) {
      blocks.push({ type: `h${h[1].length}`, text: h[2].trim() });
      i += 1;
      continue;
    }

    // Blockquote (consecutive > lines)
    if (/^\s*>\s?/.test(line)) {
      const quote = [];
      while (i < lines.length && /^\s*>\s?/.test(lines[i])) {
        quote.push(lines[i].replace(/^\s*>\s?/, ""));
        i += 1;
      }
      blocks.push({ type: "blockquote", text: quote.join(" ").trim() });
      continue;
    }

    // Unordered list
    if (isUL(line)) {
      const items = [];
      while (i < lines.length && isUL(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s+/, "").trim());
        i += 1;
      }
      blocks.push({ type: "ul", items });
      continue;
    }

    // Ordered list
    if (isOL(line)) {
      const items = [];
      while (i < lines.length && isOL(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+\.\s+/, "").trim());
        i += 1;
      }
      blocks.push({ type: "ol", items });
      continue;
    }

    // Paragraph: gather until blank line or a block starter
    const para = [line];
    i += 1;
    while (
      i < lines.length &&
      lines[i].trim() &&
      !/^(#{1,3})\s+/.test(lines[i]) &&
      !/^\s*>\s?/.test(lines[i]) &&
      !isUL(lines[i]) &&
      !isOL(lines[i])
    ) {
      para.push(lines[i]);
      i += 1;
    }
    blocks.push({ type: "p", text: para.join(" ").trim() });
  }
  return blocks;
}

const Markdown = ({ children, className = "" }) => {
  const blocks = parseBlocks(children);
  return (
    <div
      className={`text-sm leading-[1.55] sm:text-[14.5px] sm:leading-[1.62] text-[#4A5B72] [&>*+*]:mt-2 ${className}`}
    >
      {blocks.map((b, idx) => {
        const key = `b${idx}`;
        if (b.type === "h1")
          return (
            <h3 key={key} className="text-[16px] font-bold text-[#101B2C] leading-snug">
              {renderInline(b.text, key)}
            </h3>
          );
        if (b.type === "h2")
          return (
            <h4 key={key} className="text-[15px] font-bold text-[#101B2C] leading-snug">
              {renderInline(b.text, key)}
            </h4>
          );
        if (b.type === "h3")
          return (
            <h5 key={key} className="text-[14px] font-bold text-[#22334B] leading-snug">
              {renderInline(b.text, key)}
            </h5>
          );
        if (b.type === "blockquote")
          return (
            <blockquote
              key={key}
              className="border-l-[3px] border-[#C9DFFB] pl-3 text-[#4A5B72] italic"
            >
              {renderInline(b.text, key)}
            </blockquote>
          );
        if (b.type === "ul")
          return (
            <ul key={key} className="list-disc pl-5 leading-[1.7]">
              {b.items.map((it, j) => (
                <li key={`${key}-${j}`}>{renderInline(it, `${key}-${j}`)}</li>
              ))}
            </ul>
          );
        if (b.type === "ol")
          return (
            <ol key={key} className="list-decimal pl-5 leading-[1.7]">
              {b.items.map((it, j) => (
                <li key={`${key}-${j}`}>{renderInline(it, `${key}-${j}`)}</li>
              ))}
            </ol>
          );
        return (
          <p key={key}>{renderInline(b.text, key)}</p>
        );
      })}
    </div>
  );
};

export default Markdown;
